import { TaskCm } from '../../data/models';

export type PlanningDiagnosticCategory =
  | 'canvas.action'
  | 'canvas.move'
  | 'canvas.reinit'
  | 'canvas.date'
  | 'canvas.viewport'
  | 'ui.resize'
  | 'router.navigation'
  | 'log.error'
  | 'task.reschedule'
  | 'hub.notification'
  | 'store.upsert'
  | 'store.skip-stale'
  | 'lock.employee'
  | 'rows.visible'
  | 'anomaly.jump'
  | 'anomaly.overlap'
  | 'store.missing-project'
  | 'app.unhandled-error'
  | 'graphics.error'
  | 'http.failure'
  | 'hub.connection'
  | 'screenshot.capture'
  | 'store.size';

export type PlanningDateNavSource = 'today' | 'picker' | 'restore' | 'row';

export interface PlanningDiagnosticEvent {
  t: number;
  seq: number;
  cat: PlanningDiagnosticCategory;
  taskId?: number;
  data: Record<string, unknown>;
}

export interface PlanningNotificationMetadata {
  notificationId?: string;
  producer?: string;
  emittedAtUtc?: string;
  originTraceId?: string;
  originSpanId?: string;
  requestedProjectIds?: number[];
  loadedProjectIds?: number[];
  missingProjectIds?: number[];
}

export interface TaskPositionSnapshot {
  id?: number;
  employeeId?: string;
  from?: string;
  to?: string;
  timestamp?: number;
  changeToken?: string;
  projectId?: number;
}

export type PlanningProblemType = 'jump' | 'crash' | 'detached-move';

/** A moved bar was not under the pointer on release; the dispatcher chose. */
export interface DetachedMoveStatement {
  taskId: number;
  teiljobNr?: number;
  jobNr?: number;
  choice: 'object' | 'pointer' | 'cancel';
  originalFrom: string | null;
  originalRow: number | null;
  objectFrom: string;
  objectRow: number;
  pointerFrom: string;
  pointerRow: number;
  gapPx: number;
  pannedDuringMove: boolean;
}

export interface PlanningProblemReport {
  reportId: string;
  problemType: PlanningProblemType;
  createdAt: string;
  sessionId: string;
  version: string;
  envName?: string;
  userName?: string;
  startedAt: string;
  uptimeMs: number;
  taskCount: number | null;
  canvasAction: { active: boolean; taskId?: string };
  inflightReschedules: Record<string, number>;
  extras: Record<string, unknown>;
  events: PlanningDiagnosticEvent[];
}

/** @deprecated Use PlanningProblemReport. */
export type PlanningJumpReport = PlanningProblemReport;

export function summarizeTask(
  task: Partial<TaskCm> | null | undefined,
): TaskPositionSnapshot | null {
  if (!task) {
    return null;
  }
  return {
    id: task.id,
    employeeId: task.employeeId,
    from: task.from,
    to: task.to,
    timestamp: task.timestamp,
    changeToken: task.changeToken,
    projectId: task.projectId,
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const IMMEDIATE_CORRECTION_MS = 3000;
export const VIEWPORT_REINIT_MS = 8 * 1000;

export const PINNED_DIAGNOSTIC_CATEGORIES: ReadonlySet<PlanningDiagnosticCategory> =
  new Set([
    'canvas.move',
    'canvas.reinit',
    'canvas.date',
    'anomaly.jump',
    'anomaly.overlap',
    'store.skip-stale',
    'task.reschedule',
    'log.error',
    'router.navigation',
    'ui.resize',
    'store.missing-project',
    'app.unhandled-error',
    'graphics.error',
    'http.failure',
    'hub.connection',
    'screenshot.capture',
  ]);

export interface JumpPosition {
  employeeId: string;
  employeeName?: string;
  from: string;
}

export interface JumpStatement {
  teiljobNr: number;
  jobNr: number;
  taskId?: number;
  expected: JumpPosition;
  actual: JumpPosition | null;
  comment: string;
}

export interface CrashStatement {
  action: string;
  outcome: string;
}

export interface LastCanvasMoveEnd {
  taskId: number;
  t: number;
  employeeId: string;
  employeeName?: string;
  from: string;
  to: string;
  mappingMismatch?: boolean;
  start?: {
    employeeId: string;
    employeeName?: string;
    from: string;
    to: string;
  };
}

export interface TaskMutationDescription {
  before: TaskPositionSnapshot | null;
  after: TaskPositionSnapshot | null;
  employeeChanged: boolean;
  timeDeltaMs: number | null;
  timeJump: boolean;
  staleIncoming: boolean;
  anomaly: boolean;
}

export function describeTaskMutation(
  before: TaskPositionSnapshot | null,
  after: TaskPositionSnapshot | null,
): TaskMutationDescription {
  const employeeChanged = !!before && !!after && before.employeeId !== after.employeeId;
  const fromBefore = before?.from ? Date.parse(before.from) : NaN;
  const fromAfter = after?.from ? Date.parse(after.from) : NaN;
  const timeDeltaMs =
    Number.isFinite(fromBefore) && Number.isFinite(fromAfter)
      ? fromAfter - fromBefore
      : null;
  const timeJump = timeDeltaMs != null && Math.abs(timeDeltaMs) >= DAY_MS;
  const staleIncoming =
    before?.timestamp != null &&
    after?.timestamp != null &&
    after.timestamp < before.timestamp;
  return {
    before,
    after,
    employeeChanged,
    timeDeltaMs,
    timeJump,
    staleIncoming,
    anomaly: employeeChanged && timeJump,
  };
}

export function jumpPositionsEqual(
  left?: JumpPosition | null,
  right?: JumpPosition | null,
): boolean {
  if (!left || !right) {
    return false;
  }
  return (
    left.employeeId === right.employeeId && diagnosticTimesEqual(left.from, right.from)
  );
}

export function diagnosticTimesEqual(left?: string, right?: string): boolean {
  if (!left || !right) {
    return left === right;
  }
  const leftMs = Date.parse(left);
  const rightMs = Date.parse(right);
  if (Number.isFinite(leftMs) && Number.isFinite(rightMs)) {
    return leftMs === rightMs;
  }
  return left === right;
}

export function stringifyDiagnosticTime(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  const withJson = value as { toJSON?: () => string; toISOString?: () => string };
  if (typeof withJson.toJSON === 'function') {
    try {
      const json = withJson.toJSON();
      if (typeof json === 'string' && json) {
        return json;
      }
    } catch {
      // ignore
    }
  }
  if (typeof withJson.toISOString === 'function') {
    try {
      return withJson.toISOString();
    } catch {
      // ignore
    }
  }
  return undefined;
}

export function isJumpAnomaly(
  source: string,
  mutation: TaskMutationDescription,
  options: {
    overwriteDuringMove?: boolean;
    lastMoveEnd?: LastCanvasMoveEnd | null;
  } = {},
): boolean {
  if (options.overwriteDuringMove) {
    return true;
  }
  if (mutation.staleIncoming) {
    return true;
  }
  if (source === 'optimistic') {
    return false;
  }
  const after = mutation.after;
  const last = options.lastMoveEnd;
  if (last && after && after.id === last.taskId) {
    const matchesDrop =
      after.employeeId === last.employeeId && diagnosticTimesEqual(after.from, last.from);
    if (matchesDrop) {
      return false;
    }
    if (mutation.employeeChanged || mutation.timeJump) {
      return true;
    }
  }
  return mutation.anomaly === true;
}
